import { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from 'react-i18next'
import { useAuthState } from "@/hooks/useAuth"
import { addVoteToFlash} from "../../lib/voteData"
import { useUserVotes, triggerVoteUpdate } from "../../hooks/useUserVotes"
import type { PortfolioItem } from "@/types/models"
import { AuthModal } from "@/components/auth/AuthModal"
import { FirstVotePopup } from "./FirstVotePopup"

interface CraveButtonProps {
  item: PortfolioItem
  weekIndex: number
  votingEnded?: boolean
}

export function CraveButton({ item, weekIndex, votingEnded }: CraveButtonProps) {
  const { t } = useTranslation()
  const { user } = useAuthState()
  const [isVoting, setIsVoting] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showFirstVotePopup, setShowFirstVotePopup] = useState(false)
  const [isLastVote, setIsLastVote] = useState(false)
  const [currentVoteCount, setCurrentVoteCount] = useState(1)

  // Use the custom hook for vote management
  const { userVotesThisWeek, hasVotedForFlash, remainingVotes } = useUserVotes(weekIndex)
  const hasVotedForThis = hasVotedForFlash(item.id)

  // Update hasVotedForThis when item.timedVotes changes (for real-time updates)
  useEffect(() => {
    if (user) {
      // Check both timedVotes and legacy votes
      const hasVotedInTimedVotes = item.timedVotes?.some(vote => vote.userId === user.uid)
      const hasVotedInLegacyVotes = item.votes?.includes(user.uid)
      
      if ((hasVotedInTimedVotes || hasVotedInLegacyVotes) && !hasVotedForThis) {
        // This handles the case where the vote is reflected in item before our hook updates
        triggerVoteUpdate()
      }
    }
  }, [user, item.timedVotes, item.votes, hasVotedForThis])

  const handleVoteClick = () => {
    if (votingEnded) {
      return
    }
    
    if (!user) {
      console.log("Showing auth modal")
      setShowAuthModal(true)
      return
    }
    
    if (isVoting || hasVotedForThis || remainingVotes <= 0) return
    setShowConfirmDialog(true)
  }

  const handleVoteConfirm = async () => {
    if (!user || isVoting) return
    setShowConfirmDialog(false)
    setIsVoting(true)
    
    try {
      await addVoteToFlash(item.id, user.uid, weekIndex)

      // Update the item's timedVotes array for immediate UI feedback
      const now = new Date()
      if (item.timedVotes) {
        item.timedVotes = [...item.timedVotes, { userId: user.uid, votedAt: now }]
      } else {
        item.timedVotes = [{ userId: user.uid, votedAt: now }]
      }

      // Calculate what vote number this is (1st, 2nd, or 3rd)
      const voteNumber = userVotesThisWeek.length + 1
      const willBeLastVote = voteNumber === 3

      // Trigger global vote update to refresh all buttons
      triggerVoteUpdate()

      // Show popup for every vote
      setIsLastVote(willBeLastVote)
      setCurrentVoteCount(voteNumber)
      setShowFirstVotePopup(true)
    } catch (error) {
      console.error("Error voting:", error)
      // Show error message to user if it's the artist voting for their own flash
      if (error instanceof Error && error.message === "Artists cannot vote for their own flash") {
        alert("Artists cannot vote for their own flash")
      }
    } finally {
      setIsVoting(false)
    }
  }

  const handleAuthSuccess = () => {
    // After successful authentication, trigger the vote action
    handleVoteClick()
  }

  const getButtonText = () => {
    if (votingEnded) {
      return "Voting Ended"
    }
    
    if (!user) {
      return t('fotw.gallery.craveButton', { used: 3, total: 3 })
    }

    if (hasVotedForThis) {
      return t('fotw.gallery.craveButtonVoted')
    } else if (remainingVotes === 0) {
      return t('fotw.gallery.craveButtonFull')
    } else {
      return t('fotw.gallery.craveButton', { used: remainingVotes, total: 3 })
    }
  }

  const getButtonStyle = () => {
    if (votingEnded) {
      return "bg-muted text-muted-foreground cursor-not-allowed"
    }
    
    if (!user) {
      return "bg-primary text-primary-foreground hover:bg-primary/90"
    }

    if (hasVotedForThis) {
      return "bg-primary/50 text-primary-foreground cursor-not-allowed"
    } else if (remainingVotes === 0) {
      return "bg-muted text-muted-foreground cursor-not-allowed"
    } else {
      return "bg-primary text-primary-foreground hover:bg-primary/90"
    }
  }

  const canVote = () => {
    if (votingEnded) return false
    if (!user) return true
    return !hasVotedForThis && remainingVotes > 0
  }

  return (
    <>
      <button
        onClick={handleVoteClick}
        disabled={votingEnded || isVoting || (!user ? false : (!canVote() || hasVotedForThis))}
        className={`w-full text-sm font-medium px-4 py-3 rounded-lg transition-all ${getButtonStyle()}`}
      >
        {isVoting ? "..." : getButtonText()}
      </button>

      {/* Vote Confirmation Dialog - rendered via portal to escape stacking contexts */}
      {showConfirmDialog && createPortal(
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-[100] bg-black/50 animate-in fade-in-0"
            onClick={() => setShowConfirmDialog(false)}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none p-4">
            <div className="bg-background rounded-[var(--card-radius)] shadow-lg max-w-sm w-full animate-in zoom-in-95 pointer-events-auto">
              {/* Content */}
              <div className="p-6 space-y-4 text-center">
                {/* Title */}
                <h2 className="text-xl font-semibold text-foreground">
                  {t('fotw.gallery.confirmVote.title')}
                </h2>

                {/* Description */}
                <p className="text-base text-muted-foreground leading-relaxed">
                  {t('fotw.gallery.confirmVote.description')}
                </p>

                {/* Buttons */}
                <div className="flex flex-col gap-3 pt-2">
                  <button 
                    onClick={handleVoteConfirm} 
                    disabled={isVoting}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isVoting ? t('fotw.gallery.confirmVote.voting') : t('fotw.gallery.confirmVote.confirm')}
                  </button>
                  <button 
                    onClick={() => setShowConfirmDialog(false)}
                    className="w-full bg-muted hover:bg-muted/80 text-foreground font-medium py-3 rounded-lg transition-colors"
                  >
                    {t('fotw.gallery.confirmVote.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      <AuthModal
        isOpen={showAuthModal}
        onOpenChange={setShowAuthModal}
        onSuccess={handleAuthSuccess}
        source="crave_button"
        defaultMode="signup"
      />

      <FirstVotePopup
        isOpen={showFirstVotePopup}
        onOpenChange={setShowFirstVotePopup}
        isLastVote={isLastVote}
        currentVoteCount={currentVoteCount}
      />
    </>
  )
} 